import React, { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ProductCard from '../components/ProductsCard';
import HeaderTitle from '../components/HeaderTitle';
import { ChevronRight, Home, Star, Share2, Heart, ShoppingCart } from 'lucide-react';


const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ProductPage = ({ product, filter }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    category: searchParams.get('category') || '',
    type: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Get unique categories and types
  const categories = [...new Set(product.map(item => item.category))];
  const types = [...new Set(product.map(item => item.type))];

  // Comprehensive filtering logic
  const filteredProducts = useMemo(() => {
    return product.filter(item => {
      // Category filter
      const categoryMatch = !selectedFilters.category || 
        item.category === selectedFilters.category;
      
      // Type filter
      const typeMatch = !selectedFilters.type || 
        item.type === selectedFilters.type;
      
      // Search filter
      const searchMatch = !debouncedSearchTerm || 
        item.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase());
      
      return categoryMatch && typeMatch && searchMatch;
    });
  }, [product, selectedFilters, debouncedSearchTerm]);

  // Get types for the selected category
  const filteredTypes = useMemo(() => {
    if (!selectedFilters.category) return types;
    return [...new Set(
      product
        .filter(item => item.category === selectedFilters.category)
        .map(item => item.type)
    )];
  }, [product, selectedFilters.category]);

  // Search suggestions
  const searchSuggestions = useMemo(() => {
    if (!searchTerm) return [];
    return product
      .filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .slice(0, 5);
  }, [product, searchTerm]);

  // Handlers
  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: value === prev[filterType] ? '' : value
    }));
  };

  const handleReset = () => {
    setSelectedFilters({ category: '', type: '' });
    setSearchTerm('');
  };

  return (
    <div>
      <HeaderTitle title={"Products"}/>
      
      <section>
        <div className="mx-auto max-w-screen-xl px-4 py-1 sm:px-6 sm:py-4 lg:px-8">
        <nav className="flex items-center space-x-1 text-sm text-gray-500 mb-8">
        <Link to="/" className="hover:text-gray-700 flex items-center">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium">Products</span>
      </nav>
          {/* Mobile Filter Toggle */}
          <div className="mt-8 block lg:hidden">
            <button
              onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
              className="flex cursor-pointer items-center gap-2 border-b border-gray-400 pb-1 text-gray-900 transition hover:border-gray-600"
            >
              <span className="text-sm font-medium">
                Filters & Sorting 
                {(selectedFilters.category || selectedFilters.type) && 
                  ` (${selectedFilters.category || ''} ${selectedFilters.type || ''})`}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className={`size-4 transition-transform duration-200 ${isMobileFilterOpen ? 'rotate-180' : ''}`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>

            {/* Mobile Filter Panel */}
            {isMobileFilterOpen && (
              <div className="mt-4 border rounded-lg p-4 bg-white space-y-4">
                {/* Search Input */}
                <div className="relative">
                  <input 
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {searchSuggestions.length > 0 && searchTerm && (
                    <ul className="absolute z-10 w-full bg-white border rounded-md mt-1 shadow-lg max-h-48 overflow-y-auto">
                      {searchSuggestions.map((suggestion) => (
                        <li 
                          key={suggestion.id}
                          onClick={() => setSearchTerm(suggestion.name)}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          {suggestion.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Category Filter */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Categories</p>
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <label key={category} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedFilters.category === category}
                          onChange={() => handleFilterChange('category', category)}
                          className="size-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700 capitalize">
                          {category} ({product.filter(item => item.category === category).length})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Type Filter (only show if category is selected) */}
                {selectedFilters.category && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Types</p>
                    <div className="space-y-2">
                      {filteredTypes.map((type) => (
                        <label key={type} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedFilters.type === type}
                            onChange={() => handleFilterChange('type', type)}
                            className="size-4 rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-700 capitalize">
                            {type} ({product.filter(item => 
                              item.category === selectedFilters.category && 
                              item.type === type
                            ).length})
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reset Button */}
                {(selectedFilters.category || selectedFilters.type || searchTerm) && (
                  <button
                    onClick={handleReset}
                    className="w-full py-2 bg-gray-100 text-gray-900 rounded-md hover:bg-gray-200 transition"
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Desktop Filters */}
          <div className="mt-4 lg:mt-8 lg:grid lg:grid-cols-4 lg:items-start lg:gap-8">
            <div className="hidden space-y-4 lg:block">
              {/* Search Input */}
              <div className="mb-4 relative">
                <input 
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {searchSuggestions.length > 0 && searchTerm && (
                  <ul className="absolute z-10 w-full bg-white border rounded-md mt-1 shadow-lg max-h-48 overflow-y-auto">
                    {searchSuggestions.map((suggestion) => (
                      <li 
                        key={suggestion.id}
                        onClick={() => setSearchTerm(suggestion.name)}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        {suggestion.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Categories Filter */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Categories</p>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <label key={category} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedFilters.category === category}
                        onChange={() => handleFilterChange('category', category)}
                        className="size-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700 capitalize">
                        {category} ({product.filter(item => item.category === category).length})
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Types Filter (only show if category is selected) */}
              {selectedFilters.category && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Types</p>
                  <div className="space-y-2">
                    {filteredTypes.map((type) => (
                      <label key={type} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedFilters.type === type}
                          onChange={() => handleFilterChange('type', type)}
                          className="size-4 rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700 capitalize">
                          {type} ({product.filter(item => 
                            item.category === selectedFilters.category && 
                            item.type === type
                          ).length})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Reset Button */}
              {(selectedFilters.category || selectedFilters.type || searchTerm) && (
                <button
                  onClick={handleReset}
                  className="w-full py-2 bg-gray-100 text-gray-900 rounded-md hover:bg-gray-200 transition mt-4"
                >
                  Reset Filters
                </button>
              )}
            </div>

            {/* Product Grid */}
            <div className="lg:col-span-3">
              {filteredProducts.length > 0 ? (
                <div>
                  {/* Category and Type Display */}
                  {(selectedFilters.category || selectedFilters.type) && (
                    <div className="mb-4 text-sm text-gray-600">
                      Showing 
                      {selectedFilters.category && ` ${selectedFilters.category}`}
                      {selectedFilters.type && ` ${selectedFilters.type}`}
                      {` (${filteredProducts.length} products)`}
                    </div>
                  )}

                  <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredProducts.map((prod) => (
                      <ProductCard key={prod.id} {...prod} />
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-10 text-gray-500">
                  No products found. Try a different search or filter.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;